import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { Alert, Button, Modal } from 'react-daisyui';
import { Trans, useTranslation } from 'react-i18next';
import { useSubscriptionManagerContext } from 'src/pages/Settings/components/SubscriptionsManager/SubscriptionManager.context';
import { useACLContext } from 'src/providers/ACLProvider/ACL.hook';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { CENTS_TO_DOLLARS_CONVERSION_FACTOR } from '../../../SubscriptionManager.constants';
import { ePlanCurrency } from '../../../SubscriptionManager.enums';
import CardSelector from '../../CardSelector/CardSelector.component';

export default function ManageWatermarkModal() {
  const {
    selectedPaymentMethod,
    toggleAddPaymentMethodModal,
    showManageWatermarkModal,
    toggleManageWatermarkModal,
    updateWatermarkPlan,
    updatingPlan,
    products,
  } = useSubscriptionManagerContext();
  const { subscription } = useSubscriptionContext();
  const { watermarkRemoval: watermarkRemovalIsEnabled } = useACLContext();
  const { t } = useTranslation('settings/SubscriptionManager');

  const [watermarkPlanRemoval, setWatermarkPlanRemoval] = useState<boolean>(
    !watermarkRemovalIsEnabled,
  );

  const longDateConfig: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };

  const conversionFormattedDate = subscription.endDate.toLocaleString(
    'es-MX',
    longDateConfig,
  );

  // Using a fixed cost for watermark removal based on existing code
  const watermarkCost =
    products?.allRelatedProducts?.find(
      (product) => product.productType === 'LEADBOT_PREMIUM',
    )?.price ||
    (subscription.currency === ePlanCurrency.MXN && 2500 * 21) ||
    2500;

  return (
    <Modal
      open={showManageWatermarkModal}
      backdrop
      onClose={() => {
        toggleManageWatermarkModal(false);
        setWatermarkPlanRemoval(watermarkRemovalIsEnabled);
      }}
      className="rounded-lg p-0 min-w-[38.5rem]"
      data-testid="subscription-modal"
    >
      <Modal.Header className="flex justify-between items-center mb-0 p-4 border-b">
        <h1 className="text-lg w-full">{t('addOns.manageWatermark.title')}</h1>
        <XMarkIcon
          onClick={() => {
            toggleManageWatermarkModal(false);
            setWatermarkPlanRemoval(watermarkRemovalIsEnabled);
          }}
          className="size-6"
        />
      </Modal.Header>
      <Modal.Body className="py-6 px-4 form-control gap-4">
        {!watermarkRemovalIsEnabled ? (
          <div className="flex flex-col gap-1">
            <p>{t('addOns.manageWatermark.paymentMethod')}</p>
            {isEmpty(selectedPaymentMethod.paymentMethodId) ? (
              <Button
                className="SubscriptionCheckout__paymentMethods__addPaymentMethodButton"
                onClick={() => toggleAddPaymentMethodModal(true)}
                color="ghost"
                fullWidth
                data-testid="add-payment-method-button"
              >
                {t('page.SubscriptionCheckout.addPaymentMethodButton')}
              </Button>
            ) : (
              <>
                <CardSelector />
                <Button
                  onClick={() => toggleAddPaymentMethodModal(true)}
                  variant="link"
                  size="sm"
                  className="text-neutral-content w-fit px-0"
                >
                  {t('page.SubscriptionCheckout.addPaymentMethod')}
                </Button>
              </>
            )}
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          {!watermarkRemovalIsEnabled ? (
            <p className="text-sm text-neutral-500">
              {t('addOns.manageWatermark.proportionalCharge')}
              <br />
              {t('addOns.manageWatermark.proportionalChargeDescription', {
                date: conversionFormattedDate,
                cost: watermarkCost / CENTS_TO_DOLLARS_CONVERSION_FACTOR,
                currency: subscription.currency,
                frequency: t(
                  `subscriptionOverview.frequency.${subscription.frequency}`,
                ),
              })}
            </p>
          ) : (
            <>
              <p>{t('addOns.manageWatermark.deactivationWarning')}</p>
              <Alert
                icon={<InformationCircleIcon className="size-6 text-primary" />}
                className="border-primary p-2"
              >
                <span className="text-sm">
                  <Trans
                    i18nKey="settings/SubscriptionManager:addOns.manageWatermark.deactivationAlert"
                    values={{ date: conversionFormattedDate }}
                    components={{
                      strong: <strong />,
                    }}
                  />
                </span>
              </Alert>
            </>
          )}
        </div>
      </Modal.Body>
      <Modal.Actions className="p-4 bg-base-300 items-center mt-0" data-testid="modal-actions">
        {!watermarkRemovalIsEnabled ? (
          <>
            <p className="font-bold text-xl">
              {t('addOns.manageWatermark.total', {
                cost: watermarkCost / CENTS_TO_DOLLARS_CONVERSION_FACTOR,
                currency: subscription.currency,
              })}
            </p>
            <p className="text-xs font-normal flex-1 ">
              {t('addOns.manageWatermark.monthlyRecurring', {
                frequency: t(
                  `subscriptionOverview.frequency.${subscription.frequency}`,
                ),
              })}
            </p>
          </>
        ) : null}
        <Button
          color={watermarkRemovalIsEnabled ? 'error' : 'primary'}
          size="sm"
          className="rounded"
          disabled={watermarkPlanRemoval === watermarkRemovalIsEnabled || updatingPlan}
          onClick={() => updateWatermarkPlan(watermarkPlanRemoval)}
          loading={updatingPlan}
          data-testid="modal-action-button"
        >
          {watermarkRemovalIsEnabled
            ? t('addOns.manageWatermark.confirmDeactivation')
            : t('addOns.manageWatermark.buyNow')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
